import {action, override, makeObservable, observable} from 'mobx';
import {
	StatusItem,
	IStatusItem,
	StatusItemInitProps,
} from '@netx/core-next/store';
import {UploadStatusItemStatus} from '../constants';
import type {UppyFileMeta} from '../types';

export interface IUploadStatusItem<
	D extends object = object,
	S extends string = string, // UploadStatusItemStatus,
	TMeta extends UppyFileMeta = UppyFileMeta,
> extends IStatusItem<D, S> {
	isTokenUpload: boolean;

	bytesUploaded: number;
	/** uppy file id */
	fileId: string;
	fileName: string;
	/** bytes */
	fileSize: number;
	fileType: string;
	meta: TMeta;
}

export interface UploadStatusItemInitProps<
	D extends object = object,
	S extends string = string, // UploadStatusItemStatus,
	TMeta extends UppyFileMeta = UppyFileMeta,
> extends StatusItemInitProps<D, S> {
	isTokenUpload: boolean;

	bytesUploaded: number;
	/** uppy file id */
	fileId: string;
	fileName: string;
	/** bytes */
	fileSize: number;
	fileType: string;
	meta: TMeta;
}

/** status item for uploads */
export class UploadStatusItem<
		D extends object = object,
		S extends string = string, // UploadStatusItemStatus,
		TMeta extends UppyFileMeta = UppyFileMeta,
	>
	extends StatusItem<D, S>
	implements IUploadStatusItem<D, S, TMeta>
{
	isTokenUpload: boolean;
	bytesUploaded: number;
	/** uppy file id */
	fileId: string;
	fileName: string;
	/** bytes */
	fileSize: number;
	fileType: string;
	meta: TMeta;

	/** @override */
	get canPause() {
		return (
			super.canPause &&
			this.isInStatuses([
				UploadStatusItemStatus.PENDING,
				UploadStatusItemStatus.UPLOADING,
			] as S[])
		);
	}

	/** @override */
	get canRemove() {
		return (
			!this.viable ||
			this.isInStatuses([
				UploadStatusItemStatus.PENDING,
				UploadStatusItemStatus.UPLOADING,
				UploadStatusItemStatus.RECOVERED,
			] as S[])
		);
	}

	constructor(props: UploadStatusItemInitProps<D, S, TMeta>) {
		super(props);

		this.bytesUploaded = props.bytesUploaded;
		this.fileId = props.fileId;
		this.fileName = props.fileName;
		this.fileSize = props.fileSize;
		this.fileType = props.fileType;
		this.meta = props.meta;
		this.isTokenUpload = props.isTokenUpload;

		makeObservable(this, {
			// inherited
			canPause: override,
			canRemove: override,
			setMissing: override,
			setPaused: override,

			// new
			bytesUploaded: observable,
			setBytesUploaded: action,
		});
	}

	// if the file data is no longer missing, set the status accordingly
	/** @override */
	setMissing(missing: boolean): void {
		super.setMissing(missing);
		if (!missing) this.setStatus(UploadStatusItemStatus.RECOVERED as S);
	}

	setBytesUploaded(bytesUploaded: number) {
		this.bytesUploaded = bytesUploaded;
	}

	// if resuming check if the status needs to be updated
	/** @override */
	setPaused(paused: boolean) {
		const result = super.setPaused(paused);

		if (
			!paused &&
			(
				[
					UploadStatusItemStatus.RECOVERED,
					UploadStatusItemStatus.UPLOADING,
				] as S[]
			).includes(this.status)
		) {
			this.setStatus(UploadStatusItemStatus.PENDING as S);
		}

		return result;
	}

	/** @override */
	toJson() {
		return {
			...super.toJson(),
			bytesUploaded: this.bytesUploaded,
			fileId: this.fileId,
			fileName: this.fileName,
			fileSize: this.fileSize,
			fileType: this.fileType,
			meta: {...this.meta},
		};
	}
}

export default UploadStatusItem;
